import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

// ---------------------------------------------------------------------------
// Single shared session store.
// Previously every hook (useAuth, useIsAdmin, useIsSuperAdmin,
// useHasAdminUiAccess) opened its own onAuthStateChange subscription plus a
// getSession() call, and the admin gate then fired three has_role RPCs. Any
// one stalled request left the admin screen stuck on its spinner. Now the
// session is resolved once, with a hard timeout, and shared by all consumers.
// ---------------------------------------------------------------------------

type AuthSnapshot = { user: User | null; session: Session | null; loading: boolean };

let snapshot: AuthSnapshot = { user: null, session: null, loading: true };
const listeners = new Set<(s: AuthSnapshot) => void>();
let started = false;

function emit(next: AuthSnapshot) {
  snapshot = next;
  listeners.forEach((l) => l(next));
}

function start() {
  if (started || typeof window === "undefined") return;
  started = true;

  supabase.auth.onAuthStateChange((_event, session) => {
    emit({ session, user: session?.user ?? null, loading: false });
  });

  const settle = (session: Session | null) =>
    emit({ session, user: session?.user ?? null, loading: false });

  // Fail closed after 3s: a stalled auth request must never hold the UI.
  const timer = setTimeout(() => {
    if (snapshot.loading) settle(null);
  }, 3000);

  supabase.auth
    .getSession()
    .then(({ data }) => settle(data.session ?? null))
    .catch(() => settle(null))
    .finally(() => clearTimeout(timer));
}

export function useAuth() {
  const [state, setState] = useState<AuthSnapshot>(snapshot);

  useEffect(() => {
    start();
    listeners.add(setState);
    setState(snapshot);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    emit({ user: null, session: null, loading: false });
  };

  return { user: state.user, session: state.session, loading: state.loading, signIn, signOut };
}

// ---------------------------------------------------------------------------
// Roles: one query per user, cached and time-bounded.
// ---------------------------------------------------------------------------

const roleCache = new Map<string, Promise<string[]>>();

export function fetchRoles(userId: string): Promise<string[]> {
  const cached = roleCache.get(userId);
  if (cached) return cached;

  const query = (async () => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (error) throw error;
    return (data ?? []).map((r: { role: string }) => r.role);
  })();

  const bounded = Promise.race([
    query,
    new Promise<string[]>((resolve) => setTimeout(() => resolve([]), 4000)),
  ]).catch(() => [] as string[]);

  roleCache.set(userId, bounded);
  return bounded;
}

export function clearRoleCache() {
  roleCache.clear();
}

function useRoles() {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<string[] | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setRoles([]);
      return;
    }
    let active = true;
    fetchRoles(user.id).then((r) => {
      if (active) setRoles(r);
    });
    return () => {
      active = false;
    };
  }, [user?.id, authLoading]);

  return { roles, loading: authLoading || roles === null, user };
}

export function useIsAdmin() {
  const { roles, loading, user } = useRoles();
  const isAdmin = !!roles?.some((r) => r === "admin" || r === "super_admin");
  return { isAdmin, loading, user };
}

export function useIsSuperAdmin() {
  const { roles, loading, user } = useRoles();
  return { isSuperAdmin: !!roles?.includes("super_admin"), loading, user };
}

const ADMIN_UI_ROLES = ["super_admin", "sales_manager", "service_manager"];

export function useHasAdminUiAccess() {
  const { roles, loading, user } = useRoles();
  const hasAccess = !!roles?.some((r) => ADMIN_UI_ROLES.includes(r));
  return { hasAccess, loading, user };
}
